import type { Partner_Partner } from '__generated__/graphql-types-do-not-use';

import CourseraPlusProductVariant from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import { logo } from 'bundles/coursera-plus/utils/courseraPlusUtils';
import type { CourseTypeMetadata } from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import { CourseType } from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import type { Course } from 'bundles/payments-common/hooks/useGetCourseById';
import type { SpecializationType } from 'bundles/payments-common/hooks/useGetSpecializationById';
import {
  COURSERA_PLUS_SUBSCRIPTION,
  COURSERA_TIER_LITE,
  CREDENTIAL_TRACK,
  CREDENTIAL_TRACK_SUBSCRIPTION_V2,
  SPECIALIZATION_PREPAID,
  SPECIALIZATION_SUBSCRIPTION,
  VERIFIED_CERTIFICATE,
} from 'bundles/payments/common/ProductType';
import type { PartnerType } from 'bundles/payments/data/usePartner';
import type { DegreeInfo } from 'bundles/payments/hooks/useDegreeProgramInfo';
import { getS12nPaymentsDisplayName } from 'bundles/s12n-common/lib/s12nProductVariantUtils';

import _t from 'i18n!nls/payments-common';

const MILLISECONDS_TO_MONTHS = 2628000000;

/**
 * Get the subtotal title for a product
 * @param {string} productType - Product type
 * @param {string} productItemId - Product item ID
 * @param {number} specializationDuration - Specialization duration
 * @param {Object} courseTypeMetadata - optional course type metadata
 * @returns {string} The subtotal title
 */
export const getSubtotalTitle = ({
  productType,
  productItemId,
  specializationDuration,
  courseTypeMetadata,
}: {
  productType: string;
  productItemId: string;
  specializationDuration?: number;
  courseTypeMetadata?: CourseTypeMetadata;
}): string => {
  const {
    MONTHLY_WITH_FREE_TRIAL,
    MONTHLY_MIDCYCLE_UPSELL_UPGRADE,
    ANNUAL_NO_FREE_TRIAL,
    ANNUAL_SEVEN_DAY_FREE_TRIAL,
    ANNUAL_SPECIAL_DISCOUNTED_PRICING_NO_FREE_TRIAL,
  } = CourseraPlusProductVariant;
  switch (productType) {
    case VERIFIED_CERTIFICATE:
      switch (courseTypeMetadata?.typeName) {
        case CourseType.RHYME_PROJECT:
          return _t('Guided Project');
        case CourseType.PROJECT:
          return _t('Project');
        case CourseType.STANDARD_COURSE:
        default:
          return _t('Course Certificate');
      }
    case SPECIALIZATION_SUBSCRIPTION:
      return _t('Monthly subscription');
    case SPECIALIZATION_PREPAID: {
      const numOfMonths = specializationDuration && Math.round(specializationDuration / MILLISECONDS_TO_MONTHS);

      if (!numOfMonths) {
        return _t('Subtotal:');
      } else if (numOfMonths === 1) {
        return _t('1 Month of Access:');
      } else {
        return _t('#{numOfMonths} Months of Access:', { numOfMonths });
      }
    }
    case COURSERA_TIER_LITE:
      return _t('Monthly subscription');
    case COURSERA_PLUS_SUBSCRIPTION:
      if (([MONTHLY_WITH_FREE_TRIAL, MONTHLY_MIDCYCLE_UPSELL_UPGRADE] as Array<string>).includes(productItemId)) {
        return _t('Monthly subscription');
      } else if (
        (
          [
            ANNUAL_NO_FREE_TRIAL,
            ANNUAL_SEVEN_DAY_FREE_TRIAL,
            ANNUAL_SPECIAL_DISCOUNTED_PRICING_NO_FREE_TRIAL,
          ] as Array<string>
        ).includes(productItemId)
      ) {
        return _t('Annual subscription');
      }
      return _t('Subscription');
    case CREDENTIAL_TRACK_SUBSCRIPTION_V2:
    case CREDENTIAL_TRACK:
      return _t('MasterTrack® Certificate');
    default:
      return _t('Subtotal:');
  }
};

/**
 * Get the partner logo url for a product
 * @param {boolean} isCourseraPlusSubscription - optional boolean to indicate if the product is a Coursera Plus subscription
 * @param {boolean} isCourseraLiteSubscription - optional boolean to indicate if the product is a Coursera Lite subscription
 * @param {boolean} isCourseraPlusPrepaid - optional boolean to indicate if the product is a Coursera Plus prepaid
 * @param {Object} partner - optional partner object
 * @returns {string} The partner logo url
 */
export const getPartnerLogo = ({
  isCourseraPlusSubscription,
  isCourseraLiteSubscription,
  isCourseraPlusPrepaid,
  partner,
}: {
  isCourseraPlusSubscription?: boolean;
  isCourseraLiteSubscription?: boolean;
  isCourseraPlusPrepaid?: boolean;
  partner?: PartnerType | Partner_Partner;
}): string | undefined => {
  if (isCourseraPlusSubscription || isCourseraPlusPrepaid) {
    return logo.PLUS_PILL_BLUE;
  } else if (isCourseraLiteSubscription) {
    return logo.LITE_PILL_BLUE;
  }

  // @ts-expect-error URL: string
  return partner?.squareLogo;
};

/**
 * Get the product name for a product
 * @param {boolean} isCourseraPlusSubscription - optional boolean to indicate if the product is a Coursera Plus subscription
 * @param {boolean} isCourseraLiteSubscription - optional boolean to indicate if the product is a Coursera Lite subscription
 * @param {boolean} isCourseraPlusPrepaid - optional boolean to indicate if the product is a Coursera Plus prepaid
 * @param {Object} course - optional course object
 * @param {Object} s12n - optional specialization object
 * @param {Object} degreeProgram - optional degree program object
 * @returns {string} The product name
 */
export const getProductName = ({
  isCourseraPlusSubscription,
  isCourseraLiteSubscription,
  isCourseraPlusPrepaid,
  course,
  s12n,
  degreeProgram,
}: {
  isCourseraPlusSubscription?: boolean;
  isCourseraLiteSubscription?: boolean;
  isCourseraPlusPrepaid?: boolean;
  course?: Course;
  s12n?: SpecializationType;
  degreeProgram?: DegreeInfo;
}): string | undefined => {
  if (isCourseraPlusSubscription) {
    return _t('Coursera Plus Subscription');
  } else if (isCourseraLiteSubscription) {
    return _t('Coursera Lite Subscription');
  } else if (isCourseraPlusPrepaid) {
    return _t('Coursera Plus');
  } else if (s12n) {
    const { productVariant, name } = s12n;
    return getS12nPaymentsDisplayName(name, productVariant);
  } else {
    const product = course || degreeProgram;
    return product?.name;
  }
};
